import type {
  FreeOptionConfig,
  ModifierGroup,
  ModifierOption,
  Money,
} from "@workspace/model"

import { allGroupOptions } from "@/lib/availability"

/**
 * CLIENT-SIDE mirror of the free-option half of the pricing engine
 * (`packages/ordering/src/free-option.ts` + the size/extras helpers in
 * `modifiers.ts`). The engine itself must never be imported from a
 * `"use client"` file (CLAUDE.md / `@workspace/ordering`'s index.ts doc
 * comment), so the interactive customizer needs its own copy — same rule
 * `modifier-customizer.tsx` already follows for `optionPriceDelta`/
 * `sizeGroupOf`.
 *
 * NON-AUTHORITATIVE. Everything here exists to keep the on-screen total in
 * sync with the diner's taps as they happen (see `@/lib/option-pricing-preview`,
 * which builds the customizer's preview on these); what is actually charged is
 * re-derived server-side by `@workspace/ordering`'s `priceOptionSelection`.
 * Any change to the engine's free/swap semantics MUST be mirrored here (and
 * vice versa) or the preview and the charge diverge — see the swap-credit bug
 * in ralph/progress.txt US-027.
 */

/** Mirrors `isExtrasGroup` (modifiers.ts): the backend's `extras` / `extras:<guid>` tagging. */
export function isExtrasGroup(group: Pick<ModifierGroup, "id">): boolean {
  return group.id === "extras" || group.id.startsWith("extras:")
}

/** Mirrors `canonicalSizeId` (modifiers.ts). */
export function canonicalSizeId(option: {
  id: string
  sizeId?: string
}): string {
  return option.sizeId ?? option.id
}

/**
 * Mirrors `sizeGroupOf` (modifiers.ts) — the PRICING-side size group (the one
 * whose options carry a `sizeId`, else the first single-select group).
 * Deliberately not `isSizeGroup` from `@/lib/size-group`, which is the
 * presentation-side `size:` id convention.
 */
export function pricingSizeGroupOf(
  groups: readonly ModifierGroup[]
): ModifierGroup | undefined {
  const singles = groups.filter((group) => group.selection.kind === "single")
  return (
    singles.find((group) =>
      allGroupOptions(group).some((option) => option.sizeId)
    ) ?? singles[0]
  )
}

/** Mirrors `optionPriceDelta` (modifiers.ts): a per-size price wins over the flat delta. */
export function optionPriceDelta(
  option: ModifierOption,
  sizeId?: string
): Money {
  if (sizeId) {
    const perSize = option.priceDeltaBySizeId?.[sizeId]
    if (perSize) return perSize
  }
  return option.priceDelta
}

export interface FreeOptionLine {
  optionId: string
  quantity?: number
  negative?: boolean
}

export interface FreeOptionAvailability {
  freeOptionIds: Set<string>
  selectedFreeCapacity: number
  selectedPositiveCount: number
  swapFreeCapacity: number
  nonSelectedPositiveCount: number
}

/** Mirrors `resolveFreeOptionAvailability` (free-option.ts). */
export function resolveFreeOptionAvailability(
  config: FreeOptionConfig,
  lines: readonly FreeOptionLine[]
): FreeOptionAvailability {
  const freeOptionIds = new Set(config.freeOptionIds ?? [])
  let selectedPositiveCount = 0
  let selectedNegativeCount = 0
  let nonSelectedPositiveCount = 0

  for (const line of lines) {
    if (!line.optionId) continue
    const quantity = line.quantity ?? 1
    const isConfigured = freeOptionIds.has(line.optionId)

    if (line.negative) {
      if (isConfigured) selectedNegativeCount += quantity
      continue
    }

    if (isConfigured) {
      selectedPositiveCount += quantity
    } else {
      nonSelectedPositiveCount += quantity
    }
  }

  const baseFreeCount = Math.max(config.freeCount ?? 0, 0)
  const selectedFreeCapacity = baseFreeCount + selectedNegativeCount
  const selectedCreditsConsumedBeyondBase = Math.max(
    selectedPositiveCount - baseFreeCount,
    0
  )
  const remainingSwapCredits =
    selectedNegativeCount - selectedCreditsConsumedBeyondBase
  const swapFreeCapacity =
    config.allowSwap && remainingSwapCredits > 0 ? remainingSwapCredits : 0

  return {
    freeOptionIds,
    selectedFreeCapacity,
    selectedPositiveCount,
    swapFreeCapacity,
    nonSelectedPositiveCount,
  }
}

/** Mirrors `isOptionFree` (free-option.ts). */
export function isOptionFree(
  a: FreeOptionAvailability,
  optionId: string
): boolean {
  if (a.freeOptionIds.has(optionId)) {
    return a.selectedPositiveCount < a.selectedFreeCapacity
  }
  return a.nonSelectedPositiveCount < a.swapFreeCapacity
}

/** Mirrors `resolveFreeOptionDefaultSet` (free-option.ts). */
export function resolveFreeOptionDefaultSet(
  options: readonly ModifierOption[],
  config: FreeOptionConfig | undefined,
  defaultOptionIds?: readonly string[]
): Set<string> {
  if (config?.autoAdd) {
    const freeCount = Math.max(config.freeCount ?? 0, 0)
    const eligible = (config.freeOptionIds ?? []).filter((id) =>
      options.some((o) => o.id === id && o.available)
    )
    return new Set(eligible.slice(0, freeCount))
  }
  if (defaultOptionIds) {
    return new Set(
      options
        .filter((o) => o.available && defaultOptionIds.includes(o.id))
        .map((o) => o.id)
    )
  }
  return new Set(
    options.filter((o) => o.defaultSelected && o.available).map((o) => o.id)
  )
}
